import React, { useEffect, useState } from "react";
import { Button, Card, Input, Select, Space, Table, Tag, Tooltip } from "antd";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";

const Search = Input.Search;

const STATUS_COLORS = {
  pending: "gold",
  approved: "blue",
  invited: "purple",
  activated: "green",
};

export default function WaitlistManager() {
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [approvingId, setApprovingId] = useState("");
  const [invitingId, setInvitingId] = useState("");

  const fetchRows = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", String(pagination.current));
    params.set("limit", String(pagination.pageSize));
    if (status && status !== "all") params.set("status", status);
    if (appliedQuery) params.set("q", appliedQuery);
    await request({
      url: `${apiPath.authAdminWaitlistList}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setItems(Array.isArray(data?.items) ? data.items : []);
        setPagination((prev) => ({ ...prev, total: Number(data?.total) || 0 }));
      },
      onError: (err) => {
        ShowToast(
          err?.response?.data?.message || err?.message || "Failed to load waitlist",
          Severty.ERROR,
        );
      },
    });
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, status, appliedQuery]);

  const handleApprove = async (record) => {
    setApprovingId(record?._id);
    await request({
      url: `${apiPath.authAdminWaitlistApprove}/${record?._id}/approve`,
      method: "PATCH",
      onSuccess: (data) => {
        ShowToast(data?.message || "Entry approved", Severty.SUCCESS);
        fetchRows();
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Failed to approve", Severty.ERROR);
      },
    });
    setApprovingId("");
  };

  const handleSendInvite = async (record) => {
    setInvitingId(record?._id);
    await request({
      url: `${apiPath.authAdminWaitlistInvite}/${record?._id}/invite`,
      method: "POST",
      onSuccess: (data) => {
        ShowToast(data?.message || "Invite sent", Severty.SUCCESS);
        fetchRows();
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Failed to send invite", Severty.ERROR);
      },
    });
    setInvitingId("");
  };

  const columns = [
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
      render: (v) => v || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => {
        const status = String(v || "pending");
        const label = status.charAt(0).toUpperCase() + status.slice(1);
        return <Tag color={STATUS_COLORS[status] || "default"}>{label}</Tag>;
      },
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title={record?.status === "activated" ? "Already activated" : "Approve entry"}>
            <Button
              onClick={() => handleApprove(record)}
              loading={approvingId === record?._id}
              disabled={record?.status === "activated" || record?.status === "approved" || record?.status === "invited"}
            >
              Approve
            </Button>
          </Tooltip>
          <Tooltip
            title={
              record?.status === "activated"
                ? "Already activated"
                : record?.status === "pending"
                  ? "Approve this entry before sending an invite"
                  : "Send or resend invite email"
            }
          >
            <Button
              type="primary"
              onClick={() => handleSendInvite(record)}
              loading={invitingId === record?._id}
              disabled={record?.status === "activated" || record?.status === "pending"}
            >
              Send Invite
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card title="Waitlist Manager" bordered={false} className="criclebox">
      <div style={{ marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Search
          className="waitlist-search-input"
          placeholder="Search email or company"
          value={query}
          allowClear
          onChange={(e) => {
            const nextValue = e.target.value;
            setQuery(nextValue);
            if (!nextValue) {
              setAppliedQuery("");
              setPagination((prev) => ({ ...prev, current: 1 }));
            }
          }}
          onSearch={(v) => {
            setAppliedQuery(String(v || "").trim());
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
          style={{ width: 280 }}
        />
        <Select
          style={{ width: 180 }}
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
          options={[
            { label: "All status", value: "all" },
            { label: "Pending", value: "pending" },
            { label: "Approved", value: "approved" },
            { label: "Invited", value: "invited" },
            { label: "Activated", value: "activated" },
          ]}
        />
      </div>
      <Table
        rowKey={(row) => row?._id}
        loading={loading}
        dataSource={items}
        columns={columns}
        scroll={{ x: "max-content" }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
        }}
        onChange={(p) =>
          setPagination((prev) => ({
            ...prev,
            current: p.current,
            pageSize: p.pageSize,
          }))
        }
      />
    </Card>
  );
}
